import { useMemo } from 'react';
import { CustomDomain, CustomDomainStatus, DNSVerificationError } from '@back4app2/sdk';
import StatusError from './StatusError';
import StatusSpinner from './StatusSpinner';
import StatusSuccess from './StatusSuccess';
import StatusAttention from './StatusAttention';
import toast from "react-hot-toast";
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';

const MyCustomDomainStatus = (props: { 
  customDomain: CustomDomain, 
  onVerifyDNS: (c: string) => void,
  onRedeploy?: (c: string) => void,
  isRedeploying?: boolean,
  canWrite?: boolean,
  canRead?: boolean,
  canDeploy?: boolean
}) => {
  const { customDomain: { status: domainStatus, error, id: customDomainId }, onVerifyDNS, canWrite = false, canRead = false, canDeploy = false } = props;

  const statusList = useMemo(
    () => {
      let statusList: { Icon: () => JSX.Element, text: string | JSX.Element, bgColor: string }[] = [];

      if (domainStatus === CustomDomainStatus.INITIALIZING) {
        statusList.push({ Icon: StatusSpinner, text: 'Initializing', bgColor: 'bg-old-blue/10' });
      } else if (domainStatus === CustomDomainStatus.INITIALIZED) {
        statusList.push({ Icon: StatusSpinner, text: 'Initialized', bgColor: 'bg-old-blue/10' });
      } else if (domainStatus === CustomDomainStatus.AWAITING_DNS) {
        statusList.push({ Icon: StatusAttention, text: 'Awaiting external DNS', bgColor: 'bg-alert-yellow/10' });
      } else if (domainStatus === CustomDomainStatus.GENERATING_SSL) {
        statusList.push({ Icon: StatusSuccess, text: 'Valid Configuration', bgColor: 'bg-cta-green/10' });
        statusList.push({ Icon: StatusSpinner, text: 'Generating SSL', bgColor: 'bg-old-blue/10' });
      } else if (domainStatus === CustomDomainStatus.RELEASING) {
        statusList.push({ Icon: StatusSuccess, text: 'Valid Configuration', bgColor: 'bg-cta-green/10' });
        statusList.push({ Icon: StatusSuccess, text: 'Generating SSL', bgColor: 'bg-old-blue/10' });
        statusList.push({ Icon: StatusSpinner, text: 'Releasing', bgColor: 'bg-old-blue/10' });
      } else if (domainStatus === CustomDomainStatus.READY) {
        statusList.push({ Icon: StatusSuccess, text: 'Valid Configuration', bgColor: 'bg-cta-green/10' });
        statusList.push({ Icon: StatusSuccess, text: 'Valid SSL', bgColor: 'bg-cta-green/10' });
      } else if (domainStatus === CustomDomainStatus.DELETING) {
        statusList.push({ Icon: StatusError, text: 'Deleting', bgColor: 'bg-error-red/10' });
      } else if (domainStatus === CustomDomainStatus.FAILED && error instanceof DNSVerificationError) {
        statusList.push({ 
          Icon: StatusError, 
          text: (
            <>
              External DNS Failed 
              {canWrite ? (
                <button 
                  className='outline-none border-none underline text-light-blue' 
                  onClick={() => onVerifyDNS(customDomainId)}
                >
                  Try again?
                </button>
              ) : canRead ? (
                <button 
                  className='outline-none border-none underline text-light-blue' 
                  onClick={() => {
                    toast.error('You don\'t have permission to verify DNS configuration.', {
                      className: "bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
                      icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
                      duration: 4000
                    });
                  }}
                >
                  Try again?
                </button>
              ) : null}
            </>
          ), 
          bgColor: 'bg-error-red/10' 
        });
      } else if (domainStatus === CustomDomainStatus.FAILED) {
        statusList.push({ Icon: StatusError, text: 'Failed', bgColor: 'bg-error-red/10' });
      } else if (domainStatus === CustomDomainStatus.EXPIRING) {
        statusList.push({ Icon: StatusError, text: 'Expiring', bgColor: 'bg-error-red/10' });
      } else if (domainStatus === CustomDomainStatus.EXPIRED) {
        statusList.push({ Icon: StatusError, text: 'Expired', bgColor: 'bg-error-red/10' });
        if (props.onRedeploy) {
          statusList.push({ 
            Icon: () => <></>,
            text: (
              <button
                onClick={() => {
                  if (!canDeploy) {
                    toast.error('You don\'t have permission to trigger deployments.', {
                      className: "bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
                      icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
                      duration: 4000
                    });
                    return;
                  }
                  if (!props.isRedeploying) {
                    props.onRedeploy?.(customDomainId);
                  }
                }}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs ${props.isRedeploying || !canDeploy ? 'bg-[#34506F] cursor-not-allowed' : 'bg-[#2D384B] hover:bg-[#34506F] cursor-pointer'} text-white rounded transition-colors duration-200 border border-white`}
                title={props.isRedeploying ? "Redeploying..." : !canDeploy ? "You don't have permission to trigger deployments" : "Generate a new subdomain URL"}
                disabled={props.isRedeploying}
              >
                {props.isRedeploying ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="animate-spin">
                    <path d="M2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M13 2.05C13 2.05 12.5 7 17 7C17 7 13 7.5 13 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
                {props.isRedeploying ? 'Deploying...' : 'Deploy the latest commit'}
              </button>
            ),
            bgColor: ''
          });
        }
      }
      return statusList;
    },
    [domainStatus, error, canWrite, canRead, canDeploy, onVerifyDNS, customDomainId, props]
  )
  
  return (<>
    <div className="flex items-center flex-wrap gap-[0.625rem]">
      {statusList.map(({ Icon, text, bgColor }, idx) => (
        <div key={idx} className={`${bgColor} rounded-2xl flex items-center space-x-[0.375rem] py-1 px-[0.625rem]`}>
          <Icon />
          <div className="text-xs leading-[140%]">
            {text}
          </div>
        </div>
      ))}
    </div>
  </>);
}

export default MyCustomDomainStatus;
